import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, TrendingUp, ShoppingBag, Users, Sparkles, Package } from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIOD_OPTIONS = [
  { value: "7d", label: "7 Days" },
  { value: "30d", label: "30 Days" },
  { value: "90d", label: "90 Days" },
];

const STATUS_LABELS = {
  pending_payment: "Pending Payment",
  paid: "Paid",
  accepted: "Accepted",
  in_progress: "In Progress",
  packed: "Packed",
  out_for_delivery: "Out for Delivery",
  delivered: "Delivered",
  completed: "Completed",
  cancelled: "Cancelled",
  payment_failed: "Failed",
};

const STATUS_COLORS = {
  pending_payment: "#D4859B",
  paid: "#C5DB7B",
  accepted: "#8DBBA0",
  in_progress: "#7BA7C4",
  packed: "#B89BD4",
  out_for_delivery: "#E8A87C",
  delivered: "#A8C97B",
  completed: "#6DAD8A",
  cancelled: "#C4A098",
  payment_failed: "#E07070",
};

const fmt = (v) => `Rp ${Number(v || 0).toLocaleString("id-ID")}`;

export default function AdminAnalytics() {
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/analytics?period=${period}`, { withCredentials: true })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [period]);

  const statusChartData = data
    ? Object.entries(data.status_breakdown || {}).map(([status, count]) => ({
        name: STATUS_LABELS[status] || status,
        count,
        color: STATUS_COLORS[status] || "#D4859B",
      })).sort((a, b) => b.count - a.count)
    : [];

  return (
    <div data-testid="admin-analytics">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl text-[#1C1A19]">Analytics</h1>
          <p className="font-body text-sm text-[#8A857D] mt-1">Business performance overview</p>
        </div>
        <div className="flex gap-1 p-1 bg-[#FDF8F6] border border-[#D4859B]/15 rounded-2xl">
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              data-testid={`period-${opt.value}`}
              className={`px-4 py-2 rounded-xl text-sm font-body transition-all ${
                period === opt.value ? "bg-seikat-pink text-white shadow-sm" : "text-[#4A453F] hover:text-[#1C1A19]"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-32">
          <Loader2 className="animate-spin text-seikat-pink" size={32} />
        </div>
      ) : data ? (
        <div className="space-y-8">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              icon={TrendingUp}
              label="Revenue"
              value={fmt(data.revenue?.total_revenue)}
              sub={`${data.revenue?.order_count || 0} paid orders`}
              color="#D4859B"
              data-testid="kpi-revenue"
            />
            <KpiCard
              icon={ShoppingBag}
              label="Avg Order Value"
              value={fmt(data.revenue?.avg_order)}
              sub={`Over ${period === "7d" ? "7" : period === "30d" ? "30" : "90"} days`}
              color="#C5DB7B"
            />
            <KpiCard
              icon={Users}
              label="Total Users"
              value={data.total_users?.toLocaleString()}
              sub="All time"
              color="#8DBBA0"
            />
            <KpiCard
              icon={Sparkles}
              label="Memory Bouquet AI"
              value={data.total_bouquet_requests?.toLocaleString()}
              sub={`Requests in period`}
              color="#B89BD4"
            />
          </div>

          {/* Revenue Chart */}
          <div className="bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm p-6">
            <h2 className="font-heading text-xl text-[#1C1A19] mb-1">Daily Revenue & Orders</h2>
            <p className="font-body text-xs text-[#A8A29A] mb-6">Last {period === "7d" ? "7" : period === "30d" ? "30" : "90"} days — paid orders only</p>
            {data.daily_chart?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={data.daily_chart} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0E8E4" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#A8A29A", fontFamily: "Outfit" }} tickFormatter={d => d.slice(5)} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "#A8A29A", fontFamily: "Outfit" }} tickFormatter={v => `${(v/1000).toFixed(0)}K`} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: "#A8A29A", fontFamily: "Outfit" }} />
                  <Tooltip
                    contentStyle={{ background: "#FFF", border: "1px solid rgba(212,133,155,0.2)", borderRadius: "12px", fontFamily: "Outfit", fontSize: 12 }}
                    formatter={(value, name) => name === "revenue" ? fmt(value) : value}
                  />
                  <Legend wrapperStyle={{ fontFamily: "Outfit", fontSize: 11 }} />
                  <Line yAxisId="left" type="monotone" dataKey="revenue" name="Revenue" stroke="#D4859B" strokeWidth={2} dot={false} activeDot={{ r: 5, fill: "#D4859B" }} />
                  <Line yAxisId="right" type="monotone" dataKey="orders" name="Orders" stroke="#C5DB7B" strokeWidth={2} dot={false} activeDot={{ r: 5, fill: "#C5DB7B" }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center py-16">
                <TrendingUp size={32} className="text-[#D4859B]/30 mb-3" />
                <p className="font-body text-[#A8A29A] text-sm">No paid orders in this period yet.</p>
                <p className="font-body text-[#A8A29A] text-xs mt-1">Data will appear here once orders are marked as paid.</p>
              </div>
            )}
          </div>

          {/* Status breakdown + top products */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Status breakdown */}
            <div className="bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm p-6">
              <h2 className="font-heading text-xl text-[#1C1A19] mb-1">Order Status Breakdown</h2>
              <p className="font-body text-xs text-[#A8A29A] mb-6">All orders in period</p>
              {statusChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={statusChartData} layout="vertical" margin={{ left: 0, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F0E8E4" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10, fill: "#A8A29A" }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: "#4A453F", fontFamily: "Outfit" }} width={110} />
                    <Tooltip contentStyle={{ background: "#FFF", border: "1px solid rgba(212,133,155,0.2)", borderRadius: "12px", fontFamily: "Outfit", fontSize: 12 }} />
                    <Bar dataKey="count" name="Orders" radius={[0, 6, 6, 0]}>
                      {statusChartData.map((entry, i) => (
                        <rect key={i} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <ShoppingBag size={28} className="text-[#D4859B]/30 mb-2" />
                  <p className="font-body text-[#A8A29A] text-sm">No orders in this period.</p>
                </div>
              )}
            </div>

            {/* Top products */}
            <div className="bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm p-6">
              <h2 className="font-heading text-xl text-[#1C1A19] mb-1">Top Products</h2>
              <p className="font-body text-xs text-[#A8A29A] mb-5">By revenue in period</p>
              {data.top_products?.length > 0 ? (
                <div className="space-y-3">
                  {data.top_products.map((p, i) => (
                    <div key={p.product_id || i} className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-body font-bold flex-shrink-0"
                        style={{ background: i === 0 ? "#D4859B" : i === 1 ? "#C5DB7B" : "#E8DDD5", color: i < 2 ? "#FFF" : "#6A6560" }}>
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-body text-sm font-medium text-[#1C1A19] truncate">{p.name || p.product_id}</p>
                        <p className="font-body text-xs text-[#A8A29A]">{p.quantity} sold</p>
                      </div>
                      <p className="font-body text-sm font-medium text-[#1C1A19] flex-shrink-0">{fmt(p.revenue)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <Package size={28} className="text-[#D4859B]/30 mb-2" />
                  <p className="font-body text-[#A8A29A] text-sm">No sales data in this period.</p>
                </div>
              )}
            </div>
          </div>

          {/* Memory & Memory pages summary */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm p-5">
              <p className="font-body text-[10px] tracking-widest uppercase text-[#A8A29A] mb-2">Memory Bouquet Requests</p>
              <p className="font-heading text-4xl text-[#1C1A19]">{data.total_bouquet_requests?.toLocaleString() || 0}</p>
              <p className="font-body text-xs text-[#A8A29A] mt-1">AI bouquet interpretations in period</p>
            </div>
            <div className="bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm p-5">
              <p className="font-body text-[10px] tracking-widest uppercase text-[#A8A29A] mb-2">Memory Pages Created</p>
              <p className="font-heading text-4xl text-[#1C1A19]">{data.total_memory_pages?.toLocaleString() || 0}</p>
              <p className="font-body text-xs text-[#A8A29A] mt-1">New memory pages in period</p>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: `${color}22` }}>
          <Icon size={15} style={{ color }} />
        </div>
        <p className="font-body text-[10px] tracking-widest uppercase" style={{ color }}>{label}</p>
      </div>
      <p className="font-heading text-2xl text-[#1C1A19] break-all">{value}</p>
      <p className="font-body text-xs text-[#A8A29A] mt-1">{sub}</p>
    </div>
  );
}
